import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { config } from '../config';
import { identifyUser, clearUserIdentity } from '../utils/observability';
import { endSession } from '../utils/authSession';

interface UserData {
  user_id: string;
  username: string;
  name?: string;
  email?: string;
  organization_id: string;
  organization_code: string;
  role: string;
  avatar_url?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserData | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cached React Query data (capabilities via /rbac/me, records, etc.) is
  // per-user but keyed globally — clear it on login/logout so one user never
  // inherits another's cached state (e.g. stale capabilities gating the UI).
  const queryClient = useQueryClient();

  useEffect(() => {
    // Check if user is authenticated from localStorage
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setToken(storedToken);
      setUser(parsedUser);
      setIsAuthenticated(true);

      // Identify user in observability tools (HyperDX)
      void identifyUser({
        userId: parsedUser.user_id,
        userEmail: parsedUser.email,
        userName: parsedUser.name || parsedUser.username,
        organizationId: parsedUser.organization_id,
        organizationCode: parsedUser.organization_code,
        userRole: parsedUser.role,
      });
    }

    setLoading(false);
  }, []);
  
  // Resolves false when the API refuses the credentials (a 4xx); throws when it
  // cannot be reached or fails (network or 5xx), so the page can say which.
  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    let refused = false;

    try {
      const response = await fetch(`${config.API_URL}/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });
      
      if (!response.ok) {
        refused = response.status >= 400 && response.status < 500;
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }
      
      const data = await response.json();
      
      // Store auth data
      const userData: UserData = {
        user_id: data.user_id,
        username: data.username,
        // The token response carries no email; sign-in is by email, so keep the one used.
        email: data.email ?? email,
        organization_id: data.organization_id,
        organization_code: data.organization_code,
        role: data.role,
      };
      
      // Drop any cached queries from a previous session so this user's
      // capabilities/data are fetched fresh (not served stale from cache).
      queryClient.clear();

      // Before the new session's keys land, not after — sweeping afterwards
      // would erase the session just established.
      endSession();

      setUser(userData);
      setToken(data.access_token);
      setIsAuthenticated(true);

      // Save to localStorage
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');

      // Identify user in observability tools (HyperDX)
      void identifyUser({
        userId: userData.user_id,
        userEmail: userData.email,
        userName: userData.name || userData.username,
        organizationId: userData.organization_id,
        organizationCode: userData.organization_code,
        userRole: userData.role,
      });

      setLoading(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      setLoading(false);
      if (refused) return false;
      throw err;
    }
  };
  
  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);

    // Wipe cached query data so the next user doesn't inherit this session's
    // capabilities/records from the React Query cache.
    queryClient.clear();

    endSession();

    // Clear user identity in observability tools (HyperDX)
    void clearUserIdentity();
  };
  
  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      token, 
      login, 
      logout,
      loading,
      error 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};